/**
 * Domains Store
 * CD-283: My Domains list page
 * CD-284: Add/Edit domain modal
 * CD-285: Schedule configuration UI
 *
 * Manages domain state and CRUD operations
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import domainService from '@/services/domainService'
import { useBillingStore } from '@/stores/billing'

export const useDomainsStore = defineStore('domains', () => {
  // State
  const domains = ref([])
  const currentDomain = ref(null)
  const pagination = ref({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  })
  const loading = ref(false)
  const error = ref(null)
  const filter = ref({
    enabled: null
  })

  // Computed
  const enabledDomains = computed(() =>
    domains.value.filter(d => d.is_enabled)
  )

  const scheduledDomains = computed(() =>
    domains.value.filter(d => d.schedule_frequency && d.is_enabled)
  )

  const hasDomains = computed(() => domains.value.length > 0)

  /**
   * Fetch domains list with pagination
   * @param {Object} options - Fetch options
   */
  /**
   * Fetch domains list.
   *
   * @param {object} options
   * @param {number} [options.page]
   * @param {number} [options.limit]
   * @param {boolean} [options.silent] CD-498: when true, do NOT toggle
   *   `loading.value` — the existing data stays on screen and the new
   *   list swaps in once the request resolves. Used by `onActivated`
   *   refreshes on KeepAlive-cached views so users don't see a spinner
   *   flash every time they switch back to the Domains tab.
   */
  async function fetchDomains(options = {}) {
    const silent = options.silent === true
    if (!silent) {
      loading.value = true
    }
    error.value = null

    try {
      const params = {
        page: options.page || pagination.value.page,
        limit: options.limit || pagination.value.limit,
        ...filter.value
      }

      // Remove null/undefined params
      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === undefined) {
          delete params[key]
        }
      })

      const response = await domainService.listDomains(params)
      domains.value = response.domains
      pagination.value = response.pagination
      return response
    } catch (err) {
      error.value = err.detail || err.message || 'Failed to fetch domains'
      throw err
    } finally {
      if (!silent) {
        loading.value = false
      }
    }
  }

  /**
   * CD-507: Fetch every domain for the current user without mutating the
   * paginated `domains` list or `pagination` state used by the Domains page.
   * Used by pickers (e.g., New Scan "Select Domain" modal) that need the
   * full list, not just the first page.
   *
   * Returns a plain array — does NOT populate the store's `domains` ref.
   */
  async function fetchAllForPicker() {
    try {
      const response = await domainService.listDomains({ page: 1, limit: 500 })
      return response.domains || []
    } catch (err) {
      console.warn('Failed to fetch all domains for picker:', err)
      return []
    }
  }

  /**
   * Fetch single domain by ID
   * @param {number} domainId
   */
  async function fetchDomain(domainId) {
    loading.value = true
    error.value = null

    try {
      const domain = await domainService.getDomain(domainId)
      currentDomain.value = domain
      return domain
    } catch (err) {
      error.value = err.detail || err.message || 'Failed to fetch domain'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Create new domain
   * @param {Object} data - Domain data
   */
  async function createDomain(data) {
    loading.value = true
    error.value = null

    try {
      const domain = await domainService.createDomain(data)
      // Add to list if on first page
      if (pagination.value.page === 1) {
        domains.value.unshift(domain)
        pagination.value.total += 1
      }
      return domain
    } catch (err) {
      // Handle both error formats: {detail: ...} and {error: {message: ...}}
      error.value = err.error?.message || err.detail || err.message || 'Failed to create domain'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Update existing domain
   * @param {number} domainId
   * @param {Object} data - Update data
   */
  async function updateDomain(domainId, data) {
    loading.value = true
    error.value = null

    try {
      const updated = await domainService.updateDomain(domainId, data)
      // Update in list
      const index = domains.value.findIndex(d => d.id === domainId)
      if (index !== -1) {
        domains.value[index] = updated
      }
      // Update current if viewing
      if (currentDomain.value?.id === domainId) {
        currentDomain.value = updated
      }
      return updated
    } catch (err) {
      error.value = err.detail || err.message || 'Failed to update domain'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Delete domain
   * @param {number} domainId
   */
  async function deleteDomain(domainId) {
    loading.value = true
    error.value = null

    try {
      await domainService.deleteDomain(domainId)
      // Remove from list
      domains.value = domains.value.filter(d => d.id !== domainId)
      pagination.value.total -= 1
      // Clear current if viewing
      if (currentDomain.value?.id === domainId) {
        currentDomain.value = null
      }
    } catch (err) {
      error.value = err.detail || err.message || 'Failed to delete domain'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Toggle domain enabled status
   * @param {number} domainId
   */
  async function toggleDomain(domainId) {
    const domain = domains.value.find(d => d.id === domainId)
    if (!domain) return

    loading.value = true
    error.value = null

    try {
      let updated
      if (domain.is_enabled) {
        updated = await domainService.disableDomain(domainId)
      } else {
        updated = await domainService.enableDomain(domainId)
      }
      // Update in list
      const index = domains.value.findIndex(d => d.id === domainId)
      if (index !== -1) {
        domains.value[index] = updated
      }
      return updated
    } catch (err) {
      error.value = err.detail || err.message || 'Failed to toggle domain'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Initiate scan for domain
   * @param {number} domainId
   *
   * CD-502: Refresh billing balance in the global store so the header credit
   * counter updates immediately after the 1-credit deduction, without the
   * user needing to refresh the page.
   */
  async function scanDomain(domainId) {
    loading.value = true
    error.value = null

    try {
      const result = await domainService.scanDomain(domainId)
      // Update last_scan_at
      const index = domains.value.findIndex(d => d.id === domainId)
      if (index !== -1) {
        domains.value[index].last_scan_at = new Date().toISOString()
      }

      // CD-502: Refresh header balance immediately after credit deduction.
      try {
        const billingStore = useBillingStore()
        await billingStore.fetchBalance()
      } catch (balanceErr) {
        console.warn('Failed to refresh balance after scan start:', balanceErr)
      }

      return result
    } catch (err) {
      error.value = err.detail || err.message || 'Failed to start scan'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Set filter and refresh
   * @param {Object} newFilter
   */
  async function setFilter(newFilter) {
    filter.value = { ...filter.value, ...newFilter }
    pagination.value.page = 1
    return fetchDomains()
  }

  /**
   * Go to page
   * @param {number} page
   */
  async function goToPage(page) {
    pagination.value.page = page
    return fetchDomains()
  }

  /**
   * Clear error
   */
  function clearError() {
    error.value = null
  }

  /**
   * Reset store
   */
  function reset() {
    domains.value = []
    currentDomain.value = null
    pagination.value = { page: 1, limit: 20, total: 0, pages: 0 }
    loading.value = false
    error.value = null
    filter.value = { enabled: null }
  }

  return {
    // State
    domains,
    currentDomain,
    pagination,
    loading,
    error,
    filter,

    // Computed
    enabledDomains,
    scheduledDomains,
    hasDomains,

    // Actions
    fetchDomains,
    fetchAllForPicker,
    fetchDomain,
    createDomain,
    updateDomain,
    deleteDomain,
    toggleDomain,
    scanDomain,
    setFilter,
    goToPage,
    clearError,
    reset
  }
})
